import {
  DEFAULT_CUSTOMER_PERMISSIONS,
  type CustomerDashboardSection,
  type CustomerPermissionAction,
  type CustomerPermissions,
  type OrganizationPermissionOverride,
  type UserPermissionOverride,
} from '@/types/permissions';

/**
 * Pure customer-permission resolution, shared by the Firestore queries and the
 * beneficiary gates (dashboard, navigation, protected routes, support groups).
 *
 * Hierarchy (see queries/permissions.ts for the full description):
 *   personal override > general "OFF" > organization override > general default.
 *
 * Every function returns fresh objects: nothing here mutates its inputs or
 * DEFAULT_CUSTOMER_PERMISSIONS (the previous inline helpers shallow-copied the
 * general permissions and then wrote into the shared `sections` object, which
 * leaked one organization's overrides into the next resolution).
 */

type PartialCustomerPermissions = {
  sections?: Partial<CustomerPermissions['sections']>;
  actions?: Partial<CustomerPermissions['actions']>;
};

export interface CustomerPermissionSources {
  defaultPermissions: PartialCustomerPermissions;
  userOverrides?: Pick<UserPermissionOverride, 'userId' | 'permissions'>[];
  organizationOverrides?: Pick<
    OrganizationPermissionOverride,
    'organizationId' | 'permissions' | 'overriddenAt'
  >[];
}

/** Fill any missing key from DEFAULT_CUSTOMER_PERMISSIONS (all enabled). */
export function mergeWithDefaults(
  permissions: PartialCustomerPermissions | null | undefined
): CustomerPermissions {
  return {
    sections: {
      ...DEFAULT_CUSTOMER_PERMISSIONS.sections,
      ...(permissions?.sections ?? {}),
    },
    actions: {
      ...DEFAULT_CUSTOMER_PERMISSIONS.actions,
      ...(permissions?.actions ?? {}),
    },
  };
}

export function applyOverrideRules(
  personal: CustomerPermissions | null,
  general: CustomerPermissions,
  organizational: CustomerPermissions | null
): CustomerPermissions {
  const result: CustomerPermissions = {
    sections: { ...general.sections },
    actions: { ...general.actions },
  };

  if (organizational) {
    (Object.keys(organizational.sections) as (keyof CustomerPermissions['sections'])[]).forEach(
      (key) => {
        // General "OFF" overrides Organizational "ON"
        result.sections[key] =
          general.sections[key] === false ? false : organizational.sections[key];
      }
    );
    (Object.keys(organizational.actions) as (keyof CustomerPermissions['actions'])[]).forEach(
      (key) => {
        result.actions[key] =
          general.actions[key] === false ? false : organizational.actions[key];
      }
    );
  }

  if (personal) {
    // Personal ON/OFF overrides everything
    Object.assign(result.sections, personal.sections);
    Object.assign(result.actions, personal.actions);
  }

  return result;
}

function toMillis(value: unknown): number {
  if (value instanceof Date) return value.getTime();
  if (value && typeof value === 'object') {
    const v = value as { toMillis?: () => number; seconds?: number };
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
  }
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
}

function personalFor(sources: CustomerPermissionSources, userId: string) {
  const override = (sources.userOverrides ?? []).find((o) => o.userId === userId);
  return override ? mergeWithDefaults(override.permissions) : null;
}

/**
 * Effective permissions for a beneficiary viewing a specific organization
 * (the active / selected organization). Mirrors mobile PermissionsContext,
 * which always resolves against the active org.
 */
export function resolveCustomerPermissionsForOrganization(
  sources: CustomerPermissionSources,
  userId: string,
  organizationId: string
): CustomerPermissions {
  const orgOverride = (sources.organizationOverrides ?? []).find(
    (o) => o.organizationId === organizationId
  );
  return applyOverrideRules(
    personalFor(sources, userId),
    mergeWithDefaults(sources.defaultPermissions),
    orgOverride ? mergeWithDefaults(orgOverride.permissions) : null
  );
}

/**
 * Legacy org-agnostic resolution: uses the most recent override among all of
 * the user's organizations. Only for callers with no organization context.
 */
export function resolveCustomerPermissionsAcrossOrganizations(
  sources: CustomerPermissionSources,
  userId: string,
  userOrganizationIds: string[]
): CustomerPermissions {
  const applicable = (sources.organizationOverrides ?? []).filter((o) =>
    userOrganizationIds.includes(o.organizationId)
  );
  const mostRecent = applicable.reduce<(typeof applicable)[number] | null>(
    (latest, current) =>
      !latest || toMillis(current.overriddenAt) > toMillis(latest.overriddenAt) ? current : latest,
    null
  );
  return applyOverrideRules(
    personalFor(sources, userId),
    mergeWithDefaults(sources.defaultPermissions),
    mostRecent ? mergeWithDefaults(mostRecent.permissions) : null
  );
}

/**
 * Permissions for things that live OUTSIDE any organization (a personal
 * support group): the platform default and the user's own override only.
 * Owner decision (2026-10-07): an organization's settings must not block a
 * member's personal groups; that call stays with the super admin.
 * Mirrors mobile getUserPersonalPermissions.
 */
export function resolvePersonalCustomerPermissions(
  sources: CustomerPermissionSources,
  userId: string
): CustomerPermissions {
  return applyOverrideRules(
    personalFor(sources, userId),
    mergeWithDefaults(sources.defaultPermissions),
    null
  );
}

/** Mobile semantics: only an explicit `false` hides a section. */
export function isSectionEnabled(
  permissions: PartialCustomerPermissions | null | undefined,
  section: CustomerDashboardSection
): boolean {
  return permissions?.sections?.[section] !== false;
}

export function isActionEnabled(
  permissions: PartialCustomerPermissions | null | undefined,
  action: CustomerPermissionAction
): boolean {
  return permissions?.actions?.[action] !== false;
}
